import { useEffect } from "react"

import { App, Checkbox, Divider, Flex, Form, Spin, Tag, Tooltip, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useLocation, useOutletContext } from "react-router-dom"

import Icon from "../../Icon"
import { subStepsStorage } from "../../Utils/onboarding/setStatusSubSteps"
import SelectComponent from "../Select"

const { Text, Link } = Typography

const FilterTag = ({ tag, closable, onDelete, style, theme, ...props }) => (
  <Tag
    bordered={theme === "dark"}
    color="processing"
    closable={closable}
    onClose={onDelete}
    closeIcon={
      <Icon type={"cross_small"} style={{ fontSize: 20, lineHeight: 1, color: "#8aa7f4" }} />
    }
    style={{
      fontSize: 12,
      fontWeight: 500,
      borderRadius: 6,
      padding: "0px 4px 0px 10px",
      margin: "0",
      textWrap: "balance",
      display: "flex",
      alignItems: "center",
      color: "#1448CB",
      background: theme === "dark" ? "none" : "#F2F6FF",
      ...style
    }}
    {...props}
  >
    {tag}
  </Tag>
)

function BillingDescriptorsForm({
  form,
  onSubmit,
  isActiveRDR,
  defaultValue,
  isAdmin,
  errorDescriptors,
  errorBinCodes,
  errorCaidCodes,
  theme,
  createBA,
  selectedPayment,
  subStep,
  estimatedDescriptors,
  suggestionsLoading
}) {
  const { pathname } = useLocation()
  const { notification } = App.useApp()
  const { t } = useTranslation()
  const merchant_descriptor = Form.useWatch("merchant_descriptor", form)
  const visa_arn_codes = Form.useWatch("visa_arn_codes", form)
  // const dont_remember_descriptors = Form.useWatch('dont_remember_descriptors', form)
  // const dont_remember_arn_codes = Form.useWatch('dont_remember_arn_codes', form)
  const visa_bin_codes = Form.useWatch("visa_bin_codes", form)
  const visa_caid_codes = Form.useWatch("visa_caid_codes", form)
  const use_bin_caid_codes = Form.useWatch("use_bin_caid_codes", form)
  const outletContext = useOutletContext()
  const setShowSubSteps = outletContext?.setShowSubSteps
  // const dont_remember_bin_codes = Form.useWatch('dont_remember_bin_codes', form)
  // const dont_remember_caid_codes = Form.useWatch('dont_remember_caid_codes', form)
  const isMobile = useSelector((state) => state.nav.isMobile)
  const notificationConfig = {
    closeIcon: <Text>{t("Dismiss")}</Text>,
    style: { minWidth: "max-content" }
  }

  useEffect(() => {
    if (!setShowSubSteps) return

    if (subStep === 1) {
      setShowSubSteps("billing")
      return
    }

    if (subStep === 2 && isActiveRDR && !use_bin_caid_codes) {
      setShowSubSteps("bin")
      return
    }

    if (isActiveRDR && use_bin_caid_codes) {
      setShowSubSteps("arn")
    }
  }, [isActiveRDR, use_bin_caid_codes, subStep, setShowSubSteps])

  useEffect(() => {
    if (!setShowSubSteps) return undefined

    return () => {
      setShowSubSteps()
    }
  }, [setShowSubSteps])

  const validateVisaARN = (_, value) => {
    // if(dont_remember_arn_codes && !createBA) return Promise.resolve()

    const arnCodes = value?.filter((code) => code.trim() !== "") // Filtering empty values

    // We check codes for length 23, only numbers, and starting with 2 or 7
    const invalidCodes = arnCodes?.filter((code) => !/^[27]\d{22}$/.test(code))

    if (invalidCodes?.length > 0)
      return Promise.reject(
        new Error(
          t("Each ARN code must be 23 digits long, contain only digits, and start with 2 or 7")
        )
      )
    if (!isAdmin && arnCodes?.length < 2)
      return Promise.reject(new Error(t("Please enter at least 2 ARN codes")))

    return Promise.resolve()
  }

  const validateVisaBin = (_, value) => {
    // if (!value && (createBA || dont_remember_bin_codes)) return Promise.resolve()

    const binCodes = value?.filter((code) => code.trim() !== "") // Filtering empty values

    if (binCodes && binCodes.length > 0) {
      for (const code of binCodes) {
        // check if Numbers
        if (!/^\d+$/.test(code)) {
          return Promise.reject(
            new Error(t("Must contain only digits (0-9), no spaces or special characters"))
          )
        }

        // check length (6-8 symbols)
        if (code.length < 6 || code.length > 8) {
          return Promise.reject(new Error(t("Must be 6 to 8 digits long")))
        }

        // check if no start from 0
        if (code.startsWith("0")) {
          return Promise.reject(new Error(t("Cannot start with 0")))
        }

        // Valid starting digits (optional rule) (4, 5, 6 або 3)
        const firstDigit = code[0]
        if (!["4", "5", "6", "3"].includes(firstDigit)) {
          return Promise.reject(
            new Error(
              t("Need to start from 4 (Visa), 5 (MasterCard), 6 (Discover) or 3 (AmEx, JCB)")
            )
          )
        }
      }
    }

    return Promise.resolve()
  }

  const validateCaid = (_, value) => {
    // if (!value && (createBA || dont_remember_caid_codes)) return Promise.resolve();

    const caidCodes = value?.filter((code) => code.trim() !== "") // Filtering empty values

    if (caidCodes && caidCodes.length > 0) {
      const uniqueCodes = new Set()

      for (const code of caidCodes) {
        if (!code || code.trim() === "") {
          return Promise.reject(new Error(t("Enter Caid Code")))
        }

        // check length(10-16 symbols)
        if (code.length < 10 || code.length > 16) {
          return Promise.reject(
            new Error(
              t(
                "CAID length typically 15 characters, but may vary between 10-16 characters depending on the network."
              )
            )
          )
        }

        // Only uppercase letters (A-Z) and digits (0-9) are allowed.
        if (!/^[A-Z0-9]+$/.test(code)) {
          return Promise.reject(
            new Error(t("Only uppercase letters (A-Z) and digits (0-9) are allowed."))
          )
        }

        if (uniqueCodes.has(code)) {
          return Promise.reject(new Error(t("CAID must be unique")))
        }
        uniqueCodes.add(code)
      }
    }

    return Promise.resolve()
  }

  const onFinish = (values) => {
    onSubmit(values)
  }

  function handleClearError(e) {
    let field = e.target.id
    form.setFields([
      {
        name: field,
        errors: []
      }
    ])
  }

  const handleDeleteFilter = (e, field_id, value) => {
    e.preventDefault()
    if (value) {
      //if field_type === input (arr)
      const fieldValue = form.getFieldValue(field_id)
      const newFieldValue = fieldValue.filter((el) => el !== value)
      if (newFieldValue) {
        form.setFieldValue(field_id, newFieldValue)
      }
    } else {
      form.setFieldValue(field_id, null)
    }
  }

  const handleCheckValue = (value, fieldName) => {
    let error = null
    if (fieldName === "visa_arn_codes") {
      if (visa_arn_codes?.includes(value)) error = t("No duplicates allowed.")
      //if (!/^\d{20,}$/.test(value)) error = t('Each ARN code must be at least 20 digits long and contain only digits')
    }
    if (error) {
      notification.error({
        message: error,
        ...notificationConfig
      })
    }
  }

  const handleKeyDown = (event, fieldName) => {
    const { value } = event.target
    if (value?.trim()) {
      if (["Enter", ","].includes(event.key)) {
        handleCheckValue(value, fieldName)
      }
    } else {
      if (["Backspace", "Delete", ","].includes(event.key)) {
        event.preventDefault()
        event.stopPropagation()
      }
    }
  }

  const handleKeyDownArn = (event, fieldName) => {
    const { key, target, ctrlKey, metaKey } = event
    const { value } = target

    if ((ctrlKey || metaKey) && ["v", "a", "c", "r"].includes(key.toLowerCase())) {
      return
    }

    if (
      !/^\d$/.test(key) &&
      !["Enter", ",", "Backspace", "Delete", "ArrowLeft", "ArrowRight"].includes(key)
    ) {
      event.preventDefault()
    }

    if (value?.trim()) {
      if (["Enter", ","].includes(key)) {
        handleCheckValue(value, fieldName)
      }
    } else {
      if (["Backspace", "Delete", ","].includes(key)) {
        event.preventDefault()
        event.stopPropagation()
      }
    }
  }

  const handleSelect = (value, fieldName) => {
    if (fieldName === "merchant_descriptor") {
      const processedValues = value?.map((v) => {
        // Trim and convert to uppercase
        let processedValue = v.trim().toUpperCase()

        // // check if value is from defaultValue
        // const isFromDefaultValue = defaultValue?.merchant_descriptor?.includes(
        //   v.trim().toUpperCase()
        // )

        // // if value is from defaultValue, return it as is
        // const isShopifyConnected = selectedPayment.includes("shopify")
        // if (isFromDefaultValue || !isShopifyConnected) {
        //   return processedValue
        // }

        // // for new values, process prefixes
        // // Replace PP with SP
        // if (processedValue.startsWith("PP")) {
        //   processedValue = "SP" + processedValue.substring(2)
        // }

        // // Add SP prefix if not present and not empty
        // if (!processedValue.startsWith("SP") && processedValue.length > 0) {
        //   processedValue = "SP " + processedValue
        // }

        // // Fix formatting if SP is present but without space
        // if (
        //   processedValue.startsWith("SP") &&
        //   processedValue.length > 2 &&
        //   processedValue[2] !== " "
        // ) {
        //   processedValue = "SP " + processedValue.substring(2).trim()
        // }

        return processedValue
      })

      const trimUpperCaseValue = processedValues?.map((v) => v?.trim())
      // const trimUpperCaseValue = value?.map(v => v?.trim()?.toUpperCase())
      const uniqueValue = [...new Set(trimUpperCaseValue)]
      if (trimUpperCaseValue?.length !== uniqueValue?.length) {
        notification.error({
          message: t("No duplicates allowed."),
          ...notificationConfig
        })
      }
      form.setFields([{ name: fieldName, value: uniqueValue }])
    } else {
      form.setFields([{ name: fieldName, value: value }])
    }
  }

  const handleChangeCheckboxBinCaidCode = (e) => {
    const { checked } = e.target
    setShowSubSteps((p) => !p)
    form.setFieldValue("use_bin_caid_codes", checked)
    subStepsStorage.set(checked ? "arn" : "bin")
  }

  const errorMessage = (
    <span>
      <Icon type={"alert_triangle_error"} /> {t("This field is required.")}
    </span>
  )

  const suggestionsLoader = suggestionsLoading ? (
    <Flex gap={8} align="center" style={{ margin: "-16px 0 16px" }}>
      <Spin size="small" />
      <Text type="secondary">{t("Loading suggestions from your payment provider…")}</Text>
    </Flex>
  ) : null

  return (
    <Form
      onFinish={onFinish}
      //onFinishFailed={onFinishFailed}
      //validateTrigger="onSubmit"
      className={"with-default-error"}
      layout="vertical"
      form={form}
      requiredMark={true}
    >
      {subStep === 1 && (
        <>
          <Form.Item
            className="alerts-field"
            name="merchant_descriptor"
            label={t("Billing descriptor(s)")}
            preserve={true}
            tooltip={{
              title: t(
                "A billing or statement descriptor is the name your customer sees on their bank or card statement after they make a payment to you."
              ),
              icon: (
                <div>
                  <Icon className="tooltip-icon" type={"circle_info"} />
                </div>
              ),
              overlayStyle: { minWidth: 275 },
              overlayInnerStyle: { textAlign: "center" }
            }}
            rules={[
              {
                required: true,
                //  !createBA || !dont_remember_descriptors
                message: errorMessage
              }
            ]}
          >
            <SelectComponent
              size={"large"}
              suffixIcon={null}
              open={false}
              mode="tags"
              //tagRender={SelectTagRender}
              tagRender={() => null}
              tokenSeparators={[","]}
              name="merchant_descriptor"
              placeholder={t("Separate descriptor with commas")}
              onFocus={handleClearError}
              onInputKeyDown={(e) => handleKeyDown(e, "merchant_descriptor")}
              onChange={(v) => handleSelect(v, "merchant_descriptor")}
              // disabled={dont_remember_descriptors}
              className={cls("hidden-tags like-input", {
                "has-value_descriptor": !!merchant_descriptor?.length
              })}
            />
          </Form.Item>
          {suggestionsLoader}
        </>
      )}

      <Form.Item noStyle shouldUpdate={true}>
        {() => (
          <Flex gap={8} wrap="wrap" style={{ margin: "-4px 0 24px" }}>
            {merchant_descriptor?.map((el) => {
              const hasError = errorDescriptors?.includes(el)
              const isEstimated = !hasError && estimatedDescriptors?.includes(el)
              const tag = (
                <FilterTag
                  key={el}
                  tag={el}
                  closable={
                    !(pathname.includes("/configure/enrollment") && !isAdmin) ||
                    !(
                      defaultValue?.["merchant_descriptor"]?.includes(el) &&
                      !isAdmin &&
                      !defaultValue?.["temporary_descriptor"]?.includes(el)
                    )
                  }
                  onDelete={(e) => handleDeleteFilter(e, "merchant_descriptor", el)}
                  color={hasError ? "error" : isEstimated ? "warning" : undefined}
                  style={
                    hasError
                      ? { background: "#FFF4F4", color: "#9E1414" }
                      : isEstimated
                        ? { background: "#FFFBE6", color: "#874D00" }
                        : {}
                  }
                  theme={theme}
                />
              )

              return isEstimated ? (
                <Tooltip
                  key={el}
                  title={t("Suggested based on your payment provider data, please verify")}
                >
                  {tag}
                </Tooltip>
              ) : (
                tag
              )
            })}
          </Flex>
        )}
      </Form.Item>

      {/* {createBA ? <Form.Item name="dont_remember_descriptors"
                                        valuePropName="checked"
                                        initialValue={defaultValue?.dont_remember_descriptors}
                                        style={{height: 10}}
      > */}
      {/* <Checkbox name="dont_remember_descriptors" onChange={handleChangeCheckbox}>
          <Flex gap={8} align="center">
            <Text type="secondary">
              {t("I can't find the Billing descriptor and need Chargeback's assistance")}
            </Text>
            <Tooltip
              title={t("By clicking the checkbox and proceeding, the business account will be created without billing descriptors. Our team will contact you to set this up, as it's required for alerts and chargeback prevention.")}
            >
              <div className='text-with-icon'>
              <Icon type={'circle_info'} style={tooltipStyle}/>
              </div>
            </Tooltip>
          </Flex>
        </Checkbox> */}
      {/* </Form.Item> : null} */}

      {isActiveRDR && !use_bin_caid_codes && subStep === 2 ? (
        <>
          <Form.Item
            className={`alerts-field ${isMobile && "mobile-label"}`}
            name="visa_arn_codes"
            label={<span>{t("2-4 different VISA ARN codes (for Visa transactions)")}</span>}
            tooltip={{
              title: t(
                "An ARN code is a unique tracking number given to a card transaction. They often come from Visa refunds. If you don’t have any you can create them by refunding Visa transactions or submit your BIN and CAID instead."
              ),
              icon: (
                <div>
                  <Icon className="tooltip-icon" type={"circle_info"} />
                </div>
              ),
              overlayStyle: { minWidth: 275 },
              overlayInnerStyle: { textAlign: "center" }
            }}
            rules={[
              { required: !isAdmin ? true : false, message: !isAdmin ? errorMessage : null },
              { validator: validateVisaARN }
            ]}
          >
            <SelectComponent
              size={"large"}
              suffixIcon={null}
              open={false}
              mode="tags"
              tagRender={() => null}
              tokenSeparators={[","]}
              name="visa_arn_codes"
              placeholder={t("Enter ARN codes separated by commas")}
              onFocus={handleClearError}
              onInputKeyDown={(e) => handleKeyDownArn(e, "visa_arn_codes")}
              // disabled={dont_remember_arn_codes}
              className={cls("hidden-tags like-input", {
                "has-value_arn": !!visa_arn_codes?.length
              })}
            />
          </Form.Item>
          {suggestionsLoader}

          <Form.Item noStyle shouldUpdate={true}>
            {() => (
              <Flex gap={8} wrap="wrap" style={{ margin: "-4px 0 24px" }}>
                {visa_arn_codes?.map((el) => (
                  <FilterTag
                    key={el}
                    tag={el}
                    closable={
                      !(pathname.includes("/configure/enrollment") && !isAdmin) ||
                      !(defaultValue?.["visa_arn_codes"]?.includes(el) && !isAdmin)
                    }
                    onDelete={(e) => handleDeleteFilter(e, "visa_arn_codes", el)}
                    theme={theme}
                  />
                ))}
              </Flex>
            )}
          </Form.Item>

          {/* {createBA ? <Form.Item name="dont_remember_arn_codes"
                                            valuePropName="checked"
                                            initialValue={defaultValue?.dont_remember_arn_codes}
                                            style={{height: 0}}
          > */}
          {/* <Checkbox name="dont_remember_arn_codes" onChange={handleChangeCheckbox}>
              <Flex gap={8} align="center">
                <Text type="secondary">
                  {t("I can't find the ARN codes and need Chargeback's assistance")}
                </Text>
                <Tooltip
                  title={t("By clicking the checkbox and proceeding, the business account will be created without ARN codes. Our team will contact you to set this up, since it's required for RDR alerts.")}
                >
                  <div className='text-with-icon'>
                  <Icon type={'circle_info'} style={tooltipStyle}/>
                  </div>
                </Tooltip>
              </Flex>
            </Checkbox> */}
          {/* </Form.Item> : null} */}
        </>
      ) : null}

      {use_bin_caid_codes ? (
        <>
          <Form.Item
            className="alerts-field"
            name="visa_bin_codes"
            label={t("BIN codes")}
            tooltip={{
              title: t(
                "The BIN codes is the Visa BIN(s) linked to your merchant account. It ensures RDR alerts are routed and processed accurately."
              ),
              icon: (
                <div>
                  <Icon className="tooltip-icon" type={"circle_info"} />
                </div>
              ),
              overlayStyle: { minWidth: 275 },
              placement: "topLeft",
              overlayInnerStyle: { textAlign: "center" }
            }}
            rules={[
              { required: true, message: !isAdmin ? errorMessage : null },
              { validator: validateVisaBin }
            ]}
          >
            <SelectComponent
              size={"large"}
              suffixIcon={null}
              open={false}
              mode="tags"
              tagRender={() => null}
              tokenSeparators={[","]}
              name="visa_bin_codes"
              placeholder={t("Enter BIN codes separated by commas")}
              onFocus={handleClearError}
              // disabled={dont_remember_bin_codes}
              onInputKeyDown={(e) => handleKeyDownArn(e, "visa_bin_codes")}
              className={cls("hidden-tags like-input")}
            />
          </Form.Item>
          {/* {createBA ? <Form.Item name="dont_remember_bin_codes"
                                    valuePropName="checked"
                                    initialValue={defaultValue?.dont_remember_bin_codes}
                                    style={{height: 0}}
              >
              </Form.Item> : null} */}

          <Form.Item noStyle shouldUpdate={true}>
            {() => (
              <Flex gap={8} wrap="wrap" style={{ margin: "-4px 0 24px" }}>
                {visa_bin_codes?.map((el) => (
                  <FilterTag
                    key={el}
                    tag={el}
                    // color={errorBinCodes?.includes(el) && 'error'}
                    style={
                      errorBinCodes?.includes(el) ? { background: "#FFF4F4", color: "#9E1414" } : {}
                    }
                    onDelete={(e) => handleDeleteFilter(e, "visa_bin_codes", el)}
                    theme={theme}
                  />
                ))}
              </Flex>
            )}
          </Form.Item>
          <Form.Item
            className="alerts-field"
            name="visa_caid_codes"
            label={t("CAID codes")}
            tooltip={{
              title: t(
                "The CAID is a unique identifier assigned to your merchant account by your acquirer. It allows the Visa network to identify your business during automated dispute resolution"
              ),
              icon: (
                <div>
                  <Icon className="tooltip-icon" type={"circle_info"} />
                </div>
              ),
              overlayStyle: { minWidth: 275 },
              placement: "topLeft",
              overlayInnerStyle: { textAlign: "center" }
            }}
            rules={[
              { required: true, message: !isAdmin ? errorMessage : null },
              { validator: validateCaid }
            ]}
          >
            <SelectComponent
              size={"large"}
              suffixIcon={null}
              open={false}
              mode="tags"
              tagRender={() => null}
              tokenSeparators={[","]}
              name="visa_caid_codes"
              placeholder={t("Enter CAID codes separated by commas")}
              onFocus={handleClearError}
              // disabled={dont_remember_caid_codes}
              onInputKeyDown={(e) => handleKeyDown(e, "visa_caid_codes")}
              onChange={(v) => handleSelect(v, "visa_caid_codes")}
              className={cls("hidden-tags like-input")}
            />
          </Form.Item>
          {/* {createBA ? <Form.Item name="dont_remember_caid_codes"
                                    valuePropName="checked"
                                    initialValue={defaultValue?.dont_remember_caid_codes}
                                    style={{height: 0}}
              >
              </Form.Item> : null} */}

          <Form.Item noStyle shouldUpdate={true}>
            {() => (
              <Flex gap={8} wrap="wrap" style={{ margin: "-4px 0 24px" }}>
                {visa_caid_codes?.map((el) => (
                  <FilterTag
                    key={el}
                    tag={el}
                    // color={errorCaidCodes?.includes(el) && 'error'}
                    style={
                      errorCaidCodes?.includes(el)
                        ? { background: "#FFF4F4", color: "#9E1414" }
                        : {}
                    }
                    onDelete={(e) => handleDeleteFilter(e, "visa_caid_codes", el)}
                    theme={theme}
                  />
                ))}
              </Flex>
            )}
          </Form.Item>
        </>
      ) : null}
      {isActiveRDR && subStep === 2 && (
        <Divider style={{ flex: 1, minWidth: 20, margin: "26px 0" }} />
      )}
      {isActiveRDR && subStep === 2 ? (
        <Form.Item
          name="use_bin_caid_codes"
          className="visa-arn-field"
          valuePropName="checked"
          // initialValue={}
          initialValue={defaultValue?.use_bin_caid_codes}
          style={{
            borderRadius: 8,
            paddingLeft: isMobile && 1
          }}
        >
          <Checkbox
            name="use_bin_caid_codes"
            onChange={handleChangeCheckboxBinCaidCode}
            checked={!!use_bin_caid_codes}
          >
            <Text strong style={{ marginLeft: 8, fontSize: isMobile && 14 }}>
              {t("I want to submit BIN and CAID instead of ARNs")}
            </Text>
          </Checkbox>
          <Text type="secondary" strong style={{ display: "block", marginLeft: 34, marginTop: 4 }}>
            {t("Select this if you don't have any ARN codes.")}
            {/* {t("Select this if you don't have any ARN codes.")}
            <Link href={"https://help.chargeback.io/en/articles/12782785-how-to-get-bins-caid-for-rdr-alerts"}
                  target="_blank"
            >
              {' '}{t('Learn how to get your BIN and CAID')}
            </Link> */}
          </Text>
        </Form.Item>
      ) : null}

      {/*<ButtonComponent type="primary" size="large" htmlType="submit" className="btn-primary_animation" block>
        {t('Continue')}
      </ButtonComponent>*/}
    </Form>
  )
}

export default BillingDescriptorsForm
